import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { History } from "lucide-react";

const ACTION_TYPES = [
  "", "shift.upserted", "shift.updated", "shift.deleted",
  "allocation.upserted", "allocation.cleared",
  "leave.created", "leave.created.approved", "leave.approved", "leave.rejected", "leave.cancelled",
  "roster_period.published", "roster_period.locked", "roster_period.reopened", "roster_period.snapshot",
  "roster.copy_previous_week", "roster_template.applied", "roster_template.created", "roster_template.deleted",
  "export.roster_xlsx", "export.utilisation_xlsx",
];

export default function AuditPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ action: "", entity_type: "", from_date: "", to_date: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      params.limit = 100;
      const { data } = await api.get("/audit-logs", { params });
      setRows(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  return (
    <div>
      <div className="eyebrow">Governance</div>
      <h1 className="font-display text-4xl mt-1 mb-2">Audit log</h1>
      <p className="text-sm text-[#4B5773] mb-6 max-w-2xl">
        Every change to shifts, allocations, leave, roster status and exports is recorded here.
      </p>

      <Card>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Field label="Action">
            <Select value={filters.action || "any"} onValueChange={(v)=>setFilters({...filters, action: v === "any" ? "" : v})}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="audit-action-filter"><SelectValue placeholder="Any action"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any action</SelectItem>
                {ACTION_TYPES.filter(Boolean).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Entity type">
            <Input value={filters.entity_type} data-testid="audit-entity-filter"
              onChange={(e)=>setFilters({...filters, entity_type: e.target.value})}
              placeholder="e.g. shift, leave_request" className="h-10 rounded-lg"/>
          </Field>
          <Field label="From">
            <Input type="date" value={filters.from_date} data-testid="audit-from"
              onChange={(e)=>setFilters({...filters, from_date: e.target.value})} className="h-10 rounded-lg"/>
          </Field>
          <Field label="To">
            <Input type="date" value={filters.to_date} data-testid="audit-to"
              onChange={(e)=>setFilters({...filters, to_date: e.target.value})} className="h-10 rounded-lg"/>
          </Field>
        </div>

        <div className="text-xs text-[#4B5773] mb-2 flex items-center gap-2">
          <History className="h-3.5 w-3.5"/> Showing {rows.length} of {total} events
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="text-left text-[#4B5773]">
                <th className="py-3 eyebrow font-normal">When</th>
                <th className="py-3 eyebrow font-normal">Who</th>
                <th className="py-3 eyebrow font-normal">Action</th>
                <th className="py-3 eyebrow font-normal">Entity</th>
                <th className="py-3 eyebrow font-normal">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr key={r.log_id} data-testid={`audit-row-${r.log_id}`}>
                  <td className="py-3 text-[#4B5773] tabular-nums whitespace-nowrap">
                    {new Date(r.timestamp).toLocaleString()}
                  </td>
                  <td className="py-3">
                    <div className="font-medium">{r.user_name || "-"}</div>
                    <div className="text-[11px] text-[#4B5773]">{r.user_email}</div>
                  </td>
                  <td className="py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#DBE7FE] text-[#1D4ED8] whitespace-nowrap">{r.action}</span>
                  </td>
                  <td className="py-3 text-[#4B5773]">
                    {r.entity_type}
                    {r.entity_id && <div className="text-[11px] text-slate-400">{r.entity_id}</div>}
                  </td>
                  <td className="py-3 text-[11px] text-[#4B5773] max-w-md">
                    {r.metadata && Object.keys(r.metadata).length > 0 && (
                      <span>{Object.entries(r.metadata).map(([k,v]) => `${k}=${typeof v === "object" ? JSON.stringify(v).slice(0,40) : String(v).slice(0,40)}`).join(" · ")}</span>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && !loading && (
                <tr><td colSpan={5} className="py-10 text-center text-[#4B5773]">No audit events match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
